import React from "react";

// Deep enough that ms-scale batches stay inspectable inside the timeline's Template scope, whose
// domain spans the whole retained session (seconds/minutes).
const MAX_ZOOM = 2000;
const ZOOM_STEP = 1.2;
// Pointer travel below this is a click, not a pan (mouse jitter tolerance).
const DRAG_THRESHOLD_PX = 3;

/**
 * Horizontal wheel-zoom / drag-pan state for an SVG viewport: screenX = baseX * k + tx.
 * Zoom is anchored at the cursor (the point under it stays put) and clamped to [1, 200]; panning
 * clamps tx so the content edges never detach from the viewport.  Extracted from ProfileTimeline
 * and shared with FlameGraph.
 *
 * Spread `viewportHandlers` onto the scroll/drag surface.  Content click handlers should bail out
 * when `consumeDragClick()` returns true — the browser fires a click when a pan ends on top of an
 * element, and that click must not act.
 */
export function usePanZoom(width: number, viewportRef: React.RefObject<HTMLElement | null>) {
    const [k, setK] = React.useState(1);
    const [tx, setTx] = React.useState(0);
    const dragRef = React.useRef<{ startX: number; startTx: number; moved: number } | null>(null);
    const dragClickRef = React.useRef(false);

    const clampTx = (candidate: number, kk: number) => {
        const min = width - width * kk; // right edge cannot pull left of the viewport
        return Math.max(min, Math.min(0, candidate));
    };

    const onWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const rect = viewportRef.current?.getBoundingClientRect();
        const cursor = rect ? e.clientX - rect.left : width / 2;
        const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
        const newK = Math.max(1, Math.min(MAX_ZOOM, k * factor));
        // Keep the point under the cursor stationary while zooming.
        const newTx = cursor - (cursor - tx) * (newK / k);
        setK(newK);
        setTx(clampTx(newTx, newK));
    };

    const onMouseDown = (e: React.MouseEvent) => {
        dragRef.current = { startX: e.clientX, startTx: tx, moved: 0 };
        dragClickRef.current = false;
    };
    const onMouseMove = (e: React.MouseEvent) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.startX;
        dragRef.current.moved = Math.max(dragRef.current.moved, Math.abs(dx));
        setTx(clampTx(dragRef.current.startTx + dx, k));
    };
    const endDrag = () => {
        if (dragRef.current && dragRef.current.moved > DRAG_THRESHOLD_PX) dragClickRef.current = true;
        dragRef.current = null;
    };

    const reset = React.useCallback(() => { setK(1); setTx(0); }, []);

    /** True (once) when the preceding mouse-up ended a real drag — the follow-up click must not act. */
    const consumeDragClick = () => {
        const suppressed = dragClickRef.current;
        dragClickRef.current = false;
        return suppressed;
    };

    return {
        k,
        tx,
        reset,
        consumeDragClick,
        viewportHandlers: { onWheel, onMouseDown, onMouseMove, onMouseUp: endDrag, onMouseLeave: endDrag },
    };
}
